import React, { useState, useEffect, useRef, useMemo } from 'react';
import ProductCard from './ProductCard';
import { productService } from '../services/api';
import { useProducts } from '../context/ProductsContext';
import { resolveHomeFeaturedProducts } from '../utils/homeProductSections';
import {
  HOME_CAROUSEL_SECTION_LIMIT,
  bindHomeCarouselSwipe,
  getHomeCarouselGroupSize,
  getHomeCarouselSlice,
  homeCarouselAutoPausedRef,
  homeCarouselGridKey,
  showHomeCarouselMobileArrows,
} from '../utils/homeCarousel';
import '../pages/Home.css';
import './FeaturedProductsCarousel.css';

/**
 * Homepage FEATURED carousel — reused on PDP footer.
 * @param {string} [excludeProductId] — omit current PDP product from the list
 */
export default function FeaturedProductsCarousel({ excludeProductId, className = '' }) {
  const { products: catalogProducts } = useProducts();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentGroup, setCurrentGroup] = useState(0);
  const [groupSize, setGroupSize] = useState(getHomeCarouselGroupSize);

  const sliderRef = useRef(null);
  const swipeRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const featuredResponse = await productService.getHomeFeaturedProducts(
          HOME_CAROUSEL_SECTION_LIMIT
        );
        let list = resolveHomeFeaturedProducts(
          catalogProducts,
          featuredResponse,
          HOME_CAROUSEL_SECTION_LIMIT
        );
        if (excludeProductId) {
          const needle = String(excludeProductId);
          list = list.filter((p) => String(p.id ?? p._id ?? '') !== needle);
        }
        if (!cancelled) {
          setFeaturedProducts(list);
          setCurrentGroup(0);
        }
      } catch {
        if (!cancelled) setFeaturedProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [catalogProducts, excludeProductId]);

  useEffect(() => {
    const onResize = () => {
      const next = getHomeCarouselGroupSize();
      setGroupSize((prev) => {
        if (prev !== next) setCurrentGroup(0);
        return next;
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const featuredList = useMemo(
    () => (Array.isArray(featuredProducts) ? featuredProducts : []),
    [featuredProducts]
  );

  const totalGroups =
    Math.ceil(Math.min(featuredList.length, HOME_CAROUSEL_SECTION_LIMIT) / groupSize) || 1;

  const visibleProducts = useMemo(
    () => getHomeCarouselSlice(featuredList, groupSize, currentGroup),
    [featuredList, groupSize, currentGroup]
  );

  const showMobileArrows = showHomeCarouselMobileArrows(groupSize, totalGroups);

  useEffect(() => {
    if (featuredList.length <= groupSize) return undefined;
    const interval = setInterval(() => {
      if (homeCarouselAutoPausedRef.current) return;
      setCurrentGroup((prev) => (prev + 1) % totalGroups);
    }, 6000);
    return () => clearInterval(interval);
  }, [featuredList.length, groupSize, totalGroups]);

  useEffect(() => {
    const el = sliderRef.current;
    if (!el || loading) return undefined;
    return bindHomeCarouselSwipe(el, swipeRef, {
      onLeft: () => setCurrentGroup((prev) => (prev + 1) % totalGroups),
      onRight: () => setCurrentGroup((prev) => (prev - 1 + totalGroups) % totalGroups),
    });
  }, [loading, totalGroups]);

  if (loading || featuredList.length === 0) return null;

  return (
    <div className={`home featured-products-embed ${className}`.trim()}>
      <section className="featured-products featured-products--featured section featured-products--pdp">
        <div className="container">
          <h2 className="section-title">FEATURED</h2>

          <div
            className={`trending-carousel${showMobileArrows ? ' trending-carousel--with-arrows' : ''}`}
          >
            {showMobileArrows && (
              <>
                <button
                  type="button"
                  className="home-carousel-arrow home-carousel-arrow--prev"
                  aria-label="Previous featured product"
                  onClick={() =>
                    setCurrentGroup((prev) => (prev - 1 + totalGroups) % totalGroups)
                  }
                >
                  <i className="fas fa-chevron-left" aria-hidden />
                </button>
                <button
                  type="button"
                  className="home-carousel-arrow home-carousel-arrow--next"
                  aria-label="Next featured product"
                  onClick={() => setCurrentGroup((prev) => (prev + 1) % totalGroups)}
                >
                  <i className="fas fa-chevron-right" aria-hidden />
                </button>
              </>
            )}
            <div className="trending-slider trending-slider--featured" ref={sliderRef}>
              <div
                className="trending-grid trending-grid--slide-ltr"
                key={homeCarouselGridKey(groupSize, currentGroup)}
              >
                {visibleProducts.map((product) => (
                  <ProductCard
                    key={product.id || product._id}
                    product={product}
                    variant="homeCarousel"
                  />
                ))}
              </div>
            </div>

            {totalGroups > 1 && (
              <div className="trending-indicators">
                {Array.from({ length: totalGroups }).map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`trending-indicator ${index === currentGroup ? 'active' : ''}`}
                    onClick={() => setCurrentGroup(index)}
                    aria-label={`Featured products slide ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
